import { AppContext } from 'providers/AppContext';
import { useContext, useMemo, useState } from 'react';
import type { BulkCancelPreviewResponse, BulkCancelResponse } from 'types';
import type { BulkStep } from './BulkCancel.consts';
import { MAX_ENTRIES, REASON_MAX } from './BulkCancel.consts';
import { parseKennitalas } from './BulkCancel.helpers';

export type BulkCancelHook = ReturnType<typeof useBulkCancel>;

export function useBulkCancel() {
	const ctx = useContext(AppContext);
	const { API } = ctx.config;
	const [step, setStep] = useState<BulkStep>('input');
	const [busy, setBusy] = useState(false);
	const [text, setText] = useState('');
	const [flowType, setFlowType] = useState('');
	const [reason, setReason] = useState('');
	const [preview, setPreview] = useState<BulkCancelPreviewResponse | null>(null);
	const [result, setResult] = useState<BulkCancelResponse | null>(null);
	const [error, setError] = useState('');
	const [warning, setWarning] = useState('');

	const entries = useMemo(() => parseKennitalas(text), [text]);
	const canPreview = !busy && !!flowType && entries.length > 0 && entries.length <= MAX_ENTRIES;
	const canCancel = !busy && !!preview && preview.matchedProcesses > 0 && !!reason.trim();

	const doPreview = (kennitalas: string[], flow: number) => {
		setBusy(true);
		setError('');
		ctx.http
			.post(`${API.bulkProcessCancellation}/preview`, { flowType: flow, kennitalas })
			.then((data: BulkCancelPreviewResponse) => {
				setPreview(data);
				setStep('preview');
			})
			.catch(() => {
				setError(ctx.t('admin_bulk_preview_error') || 'Preview failed. Check the list and try again.');
				setStep('input');
			})
			.finally(() => setBusy(false));
	};

	const runPreview = () => {
		if (!canPreview) return;
		setWarning('');
		doPreview(entries, Number(flowType));
	};

	const runCancel = () => {
		if (!canCancel || !preview) return;
		setBusy(true);
		setError('');
		setWarning('');
		const kennitalas = preview.processes.map(process => process.kennitala);
		const payload = {
			expectedCount: preview.matchedProcesses,
			flowType: preview.flowType,
			kennitalas,
			reason: reason.trim().slice(0, REASON_MAX)
		};
		ctx.http
			.post(API.bulkProcessCancellation, payload)
			.then((data: BulkCancelResponse) => {
				setResult(data);
				setStep('done');
				setBusy(false);
			})
			.catch((err: Error) => {
				setBusy(false);
				// http.ts only surfaces the status code; on 400/409 the data changed since preview — re-run it per UI guide
				if (/\b(400|409)\b/.test(err.message)) {
					setWarning(
						ctx.t('admin_bulk_mismatch_warning') ||
							'Data changed since the preview. It has been refreshed — review and confirm again.'
					);
					doPreview(kennitalas, preview.flowType);
				} else {
					setError(ctx.t('admin_bulk_cancel_error') || 'Bulk cancel failed. Nothing was cancelled.');
				}
			});
	};

	const backToInput = () => {
		setStep('input');
		setWarning('');
	};

	const reset = () => {
		setStep('input');
		setText('');
		setFlowType('');
		setReason('');
		setPreview(null);
		setResult(null);
		setError('');
		setWarning('');
	};

	return {
		backToInput,
		busy,
		canCancel,
		canPreview,
		entries,
		error,
		flowType,
		onFlowType: (e: React.ChangeEvent<HTMLSelectElement>) => setFlowType(e.target.value),
		onReason: (e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value),
		onText: (e: React.ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value),
		preview,
		reason,
		reset,
		result,
		runCancel,
		runPreview,
		step,
		t: ctx.t,
		text,
		warning
	};
}
